import {
  Box,
  CircularProgress,
  HairlineCard,
  IconButton,
  Icons,
  iconSize,
  palette,
  Stack,
  StatusChip,
  type StatusKind,
  Typography,
  fontStacks,
} from '@munifocus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDeadlines, type DeadlineItem } from '../lib/deadlines-api';
import { localizeStatuteRef } from '../lib/file-display';
import { pickLang } from '../lib/bylaw-workflows-api';

/**
 * Rows per page.
 *
 * Three, not five: the card sits beside "Inspection freshness" on the dashboard
 * and a longer list pushed the fold down without adding information — the rows
 * are sorted most-urgent-first, so the fourth is rarely the one being looked
 * for. The rest are reachable by paging rather than collapsed into a "+N more"
 * line that could not be acted on.
 */
const PAGE_SIZE = 3;

/**
 * Caps the list at exactly `PAGE_SIZE` rows.
 *
 * Deadline rows vary in height (a long address wraps), so a fixed page count
 * alone still let the card jump between pages. This pins the body so the card
 * stays the same height whatever is on the current page.
 */
const LIST_MAX_HEIGHT = 216;

/** Urgency colour: overdue is emergency, due within three days is warning. */
function urgencyKind(item: DeadlineItem): StatusKind {
  if (item.expired) return 'emergency';
  if (item.daysRemaining <= 3) return 'warning';
  return 'pending';
}

const ACCENT: Record<'emergency' | 'warning' | 'pending', string> = {
  emergency: palette.semantic.emergency,
  warning: palette.semantic.warning,
  pending: palette.semantic.pending,
};

function dueLabel(t: ReturnType<typeof useTranslation>['t'], item: DeadlineItem): string {
  if (item.expired) return t('gov.dashboard.deadlines.overdue', { count: -item.daysRemaining });
  if (item.daysRemaining === 0) return t('gov.dashboard.deadlines.dueToday');
  return t('gov.dashboard.deadlines.daysLeft', { count: item.daysRemaining });
}

function DeadlineRow({ item }: { item: DeadlineItem }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const kind = urgencyKind(item);
  const accent = ACCENT[kind as keyof typeof ACCENT];

  return (
    <Box
      component="button"
      type="button"
      onClick={() => navigate(`/files/${item.fileId}`)}
      sx={{
        appearance: 'none',
        textAlign: 'left',
        width: '100%',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        gap: 1.25,
        alignItems: 'flex-start',
        px: 0,
        py: 1.25,
        transition: 'opacity 200ms',
        '&:hover': { opacity: 0.72 },
      }}
    >
      <Box
        aria-hidden
        sx={{
          mt: '5px',
          width: 8,
          height: 8,
          flexShrink: 0,
          borderRadius: '50%',
          backgroundColor: accent,
        }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
        >
          <Typography
            sx={{
              fontFamily: fontStacks.mono,
              fontSize: 13,
              fontWeight: 600,
              color: palette.text.secondary,
            }}
          >
            {item.reference}
          </Typography>
          <StatusChip status={kind} label={dueLabel(t, item)} />
        </Stack>
        <Typography sx={{ fontSize: 14, color: palette.text.primary, mt: 0.25 }}>
          {/* A configured by-law clock names itself; the D&U clocks are by kind. */}
          {item.label
            ? pickLang(item.label, i18n.language)
            : t(`gov.dashboard.deadlines.kind.${item.kind}`)}
        </Typography>
        <Typography sx={{ fontSize: 12, color: palette.text.muted, mt: 0.25 }}>
          {item.address} · {localizeStatuteRef(item.statuteRef)}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * X4 — the "Upcoming deadlines" dashboard widget. Surfaces every system-tracked
 * statutory clock across the officer's files (appeal, King's Bench, warrant
 * follow-up), soonest/most-overdue first, each row deep-linking to its file
 * where the officer confirms the legal action.
 */
export function UpcomingDeadlines() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useDeadlines();
  const [page, setPage] = useState(0);

  const pageCount = Math.max(1, Math.ceil(data.length / PAGE_SIZE));
  // Deadlines refresh on a poll, so a page can vanish under the officer while
  // they are looking at it. Clamping beats rendering an empty final page.
  const safePage = Math.min(page, pageCount - 1);
  const visible = data.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  return (
    <HairlineCard sx={{ p: 2.5, width: '100%' }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Icons.Clock size={iconSize.lg} color={palette.brand.accent} />
        <Typography variant="h6">{t('gov.dashboard.deadlines.title')}</Typography>
      </Stack>
      <Typography sx={{ mt: 0.25, fontSize: 12, color: palette.text.muted }}>
        {t('gov.dashboard.deadlines.systemTracked')}
      </Typography>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={22} />
        </Box>
      ) : isError ? (
        <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>
          {t('gov.dashboard.deadlines.loadError')}
        </Typography>
      ) : data.length === 0 ? (
        <Typography variant="body2" sx={{ mt: 1.5, color: palette.text.secondary }}>
          {t('gov.dashboard.deadlines.empty')}
        </Typography>
      ) : (
        <>
          <Box
            sx={{
              mt: 0.5,
              minHeight: LIST_MAX_HEIGHT,
              maxHeight: LIST_MAX_HEIGHT,
              overflowY: 'auto',
              '& > button:not(:last-of-type)': { borderBottom: `1px solid ${palette.border.line}` },
            }}
          >
            {visible.map((item) => (
              <DeadlineRow key={item.id} item={item} />
            ))}
          </Box>

          {pageCount > 1 && (
            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 1.25, alignItems: 'center', justifyContent: 'space-between' }}
            >
              <Typography sx={{ fontSize: 12, color: palette.text.muted }}>
                {t('gov.dashboard.deadlines.pageOf', {
                  page: safePage + 1,
                  pages: pageCount,
                  count: data.length,
                })}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <IconButton
                  size="small"
                  aria-label={t('common.pagination.previous')}
                  disabled={safePage === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  <Icons.ChevronLeft size={iconSize.md} />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label={t('common.pagination.next')}
                  disabled={safePage >= pageCount - 1}
                  onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                >
                  <Icons.ChevronRight size={iconSize.md} />
                </IconButton>
              </Stack>
            </Stack>
          )}
        </>
      )}
    </HairlineCard>
  );
}
